/*
 * SPDX-FileCopyrightText: 2020-2025 CERN.
 * SPDX-FileCopyrightText: 2020-2022 Northwestern University.
 * SPDX-License-Identifier: MIT
 */

import _isEmpty from "lodash/isEmpty";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import React from "react";
import { TextAreaField } from "react-invenio-forms";
import { Divider, Icon, List } from "semantic-ui-react";
import { EmbargoCheckboxField } from "./EmbargoCheckboxField";
import { EmbargoDateField } from "./EmbargoDateField";
import { i18next } from "@translations/invenio_rdm_records/i18next";

export const EmbargoAccess = ({ access, accessCommunity, metadataOnly }) => {
  const recordPublic = access.record === "public";
  const filesPublic = access.files === "public";
  const communityPublic = accessCommunity === "public";

  const filesRestricted = !metadataOnly && !filesPublic;

  const embargoActive = access.embargo?.active || false;
  const embargoUntil = access.embargo?.until;
  const embargoReason = access.embargo?.reason;
  const embargoWasLifted = !embargoActive && !_isEmpty(embargoUntil);
  const embargoEnabled = communityPublic && (!recordPublic || filesRestricted);

  const fmtDate = embargoUntil
    ? DateTime.fromISO(embargoUntil)
        .setLocale(i18next.language)
        .toLocaleString(DateTime.DATE_FULL)
    : "???";

  return (
    <List>
      <List.Item disabled={!embargoEnabled} data-testid="option-list-embargo">
        <List.Icon aria-hidden="false">
          <EmbargoCheckboxField
            fieldPath="access.embargo.active"
            checked={embargoActive}
            disabled={!embargoEnabled}
          />
        </List.Icon>

        <List.Content>
          <List.Header as="label" htmlFor="access.embargo.active">
            {i18next.t("Apply an embargo")} <Icon name="clock outline" />
          </List.Header>

          <List.Description>
            <>
              {i18next.t("Record or files protection must be")}{" "}
              <b>{i18next.t("restricted")}</b> {i18next.t("to apply an embargo.")}
            </>
          </List.Description>

          {embargoActive && (
            <>
              <Divider hidden className="rel-mb-1" />
              <EmbargoDateField fieldPath="access.embargo.until" required />
              <TextAreaField
                label={i18next.t("Embargo reason")}
                fieldPath="access.embargo.reason"
                placeholder={i18next.t(
                  "Optionally, describe the reason for the embargo."
                )}
                optimized="true"
              />
            </>
          )}
          {embargoWasLifted && (
            <>
              <Divider hidden />
              <p>
                {i18next.t(`Embargo was lifted on {{fmtDate}}.`, {
                  fmtDate: fmtDate,
                })}
              </p>
              {embargoReason && (
                <p>
                  <b>{i18next.t("Reason")}</b>: {embargoReason}.
                </p>
              )}
            </>
          )}
        </List.Content>
      </List.Item>
    </List>
  );
};

EmbargoAccess.propTypes = {
  access: PropTypes.object.isRequired,
  metadataOnly: PropTypes.bool,
  accessCommunity: PropTypes.string.isRequired,
};

EmbargoAccess.defaultProps = {
  metadataOnly: false,
};
